import { useState, useEffect } from "react";
import { useTool } from "../../../contexts/ToolContext";
import usePlannerSettings from "../../../contexts/PlannerContext";
import axios from "../../../utils/axios";
import { styled } from "@mui/material/styles";
import {
  Alert,
  Grid,
  RadioGroup,
  CardActionArea,
  Typography,
  Box,
} from "@mui/material";
import BlockBoxMask from "./BlockBoxMask";
import { useTranslation } from "react-i18next";

const BoxStyle = styled(CardActionArea)(({ theme }) => ({
  width: 96,
  height: 96,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${theme.palette.grey[500_12]}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.25,
  backgroundColor: "#fafaf9",
}));

export default function SettingPlannerTool() {
  const { toolName, onChangeTool, setIsRubber, setIsSlab } = useTool();
  const { line, oldLine, height, visibleFacesVariation } = usePlannerSettings();
  const [tools, setTools] = useState([]);
  const [blockWarning, setBlockWarning] = useState(null);
  const [heightFilter, setHeightFilter] = useState("Tutti");
  const { t } = useTranslation();

  const getBlocks = async (materialId, lineId, visibleFacesVariationId) => {
    setTools([]);
    try {
      const res = await axios.post(`/planner/materials/${materialId}/lines`);
      const response = await axios.post(
          `/planner/materials/${materialId}/variations/${visibleFacesVariationId}`
      );

      let blocks = [];
      res.data.lines.forEach((line) => {
        if (line.id == lineId) {
          const blocksLine = JSON.parse(line.blocks);
          let available = blocksLine;
          if (response.data.variation.unavailable_blocks) {
            const unavailable = JSON.parse(
                response.data.variation.unavailable_blocks
            );
            const strUnavail = unavailable.map((b) => JSON.stringify(b));
            available = blocksLine.filter(
                (b) => !strUnavail.includes(JSON.stringify(b))
            );
          }
          available.forEach((block, idx) => {
            blocks.push({
              ...block,
              name: `${block.width}x${block.length}x${block.height}`,
              niceName: `${block.width}x${block.length} H${block.height}`,
              id: idx,
            });
          });
        }
      });

      setTools(blocks);
      if (
          (line === 10 && oldLine !== 2 && oldLine !== 10) ||
          (line === 3 && oldLine !== 4 && oldLine !== 3)
      ) {
        onChangeTool(blocks[0].height, blocks[0].mask, blocks[0].name);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (e, val) => {
    setIsRubber(false);
    setIsSlab(false);
    const tool = tools[val];
    onChangeTool(tool.height, tool.mask, tool.name);

    if (
        (tool.width === 25 && tool.length === 50 && tool.height === 75) ||
        (tool.width === 50 && tool.length === 25 && tool.height === 75) ||
        (tool.width === 25 && tool.length === 50 && tool.height === 100) ||
        (tool.width === 50 && tool.length === 25 && tool.height === 100)
    ) {
      setBlockWarning(1);
    } else if (
        (tool.width === 25 && tool.length === 25 && tool.height === 75) ||
        (tool.width === 25 && tool.length === 25 && tool.height === 100)
    ) {
      setBlockWarning(2);
    } else {
      setBlockWarning(null);
    }
  };

  const isSelected = (tool) => toolName === tool;

  useEffect(() => {
    if (line != null && height != null && visibleFacesVariation != null) {
      getBlocks(line, height, visibleFacesVariation);
    }
  }, [line, height, visibleFacesVariation]);

  const uniqueHeights = [...new Set(tools.map((t) => t.height))].sort(
      (a, b) => a - b
  );

  if (uniqueHeights.length === 0) {
    return null;
  }

  const filterOptions = uniqueHeights
      .map((h) => ({ label: `H ${h}`, value: h }))
      .concat({ label: t("settings.drawer.blocks.all"), value: "Tutti" });

  const filteredTools =
      heightFilter === "Tutti"
          ? tools
          : tools.filter((t) => t.height === heightFilter);

  return (
      <Box>
        <Box sx={{ display: "flex", gap: "16px", mb: 2 }}>
          {filterOptions.map((opt) => (
              <Box
                  key={opt.value}
                  onClick={() => setHeightFilter(opt.value)}
                  sx={{
                    width: 56,
                    height: 24,
                    border: "1.5px solid #31AB56",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    transition: "background-color 0.2s, color 0.2s",
                    "&:hover": {
                      backgroundColor: "rgba(49,171,86,0.1)",
                    },
                    ...(heightFilter === opt.value
                        ? { backgroundColor: "#31AB56", color: "#fff" }
                        : { backgroundColor: "transparent", color: "#31AB56" }),
                  }}
              >
                {opt.label}
              </Box>
          ))}
        </Box>

        {blockWarning && (
            <Grid container spacing={2.5} direction="row">
              <Grid item xs={12} sx={{ minWidth: "124px", maxWidth: "124px" }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {blockWarning === 1
                      ? t("settings.drawer.blocks.size_25_warning")
                      : "Tutti i lati sono realizzati con lastre sovrapposte"}
                </Alert>
              </Grid>
            </Grid>
        )}

        <RadioGroup
            name="themePlannerTool"
            value="{tool.id}"
            onChange={handleChange}
        >
          {heightFilter === "Tutti" && uniqueHeights.length > 1 ? (
              <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
                {uniqueHeights.map((h) => (
                    <Grid
                        key={h}
                        item
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          rowGap: "16px",
                          minWidth: 98,
                        }}
                    >
                      {tools
                          .filter((t) => t.height === h)
                          .map((tool) => {
                            let src;
                            try {
                              src = `/assets/shapes/Blocco-${tool.name}.svg`;
                            } catch {
                              src = "/assets/shapes/noimage.png";
                            }
                            return (
                                <Box key={tool.id} sx={{ textAlign: "center" }}>
                                  <BoxStyle
                                      sx={{
                                        boxShadow: (theme) => theme.customShadows.z20,
                                        ...(isSelected(tool.name) && {
                                          border: "2px solid #31AB56",
                                          backgroundColor: "#e2f1e9",
                                        }),
                                      }}
                                  >
                                    <Box component="img" src={src} alt={tool.niceName} />
                                    <BlockBoxMask value={tool.id} />
                                  </BoxStyle>
                                  <Typography
                                      sx={{ fontSize: "0.7rem", color: "#000", mt: 1 }}
                                  >
                                    {tool.niceName}
                                  </Typography>
                                </Box>
                            );
                          })}
                    </Grid>
                ))}
              </Grid>
          ) : (
              <Grid container sx={{ rowGap: "16px", columnGap: "16px" }}>
                {filteredTools.map((tool) => {
                  let src;
                  try {
                    src = `/assets/shapes/Blocco-${tool.name}.svg`;
                  } catch {
                    src = "/assets/shapes/noimage.png";
                  }
                  return (
                      <Grid key={tool.id} item sx={{ width: 98, textAlign: "center" }}>
                        <BoxStyle
                            sx={{
                              boxShadow: (theme) => theme.customShadows.z20,
                              ...(isSelected(tool.name) && {
                                border: "2px solid #31AB56",
                                backgroundColor: "#e2f1e9",
                              }),
                            }}
                        >
                          <Box component="img" src={src} alt={tool.niceName} />
                          <BlockBoxMask value={tool.id} />
                        </BoxStyle>
                        <Typography
                            sx={{ fontSize: "0.7rem", color: "#000", mt: 1 }}
                        >
                          {tool.niceName}
                        </Typography>
                      </Grid>
                  );
                })}
              </Grid>
          )}
        </RadioGroup>

        <Box
            sx={{
              height: "2px",
              backgroundColor: "#31AB56",
              mt: 2,
              width: "100%",
            }}
        />
      </Box>
  );
}
